import { Component } from '@theme/component';

/**
 * Repeats announcement messages for a seamless ribbon; originals remain the only accessible copy.
 * @extends {Component<{viewport: HTMLElement, track: HTMLElement, messages: HTMLElement, toggle?: HTMLButtonElement}>}
 */
class DSAnnouncementRibbon extends Component {
  requiredRefs = ['viewport', 'track', 'messages'];
  /** @type {ResizeObserver | undefined} */
  #observer;
  #frame = 0;
  #paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #initialize() {
    this.#observer?.disconnect();
    if (this.dataset.scrolling !== 'true') return;
    this.dataset.enhanced = 'true';
    this.dataset.paused = String(this.#paused);
    this.refs.toggle?.setAttribute('aria-pressed', String(this.#paused));
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(this.refs.viewport);
    this.#observer.observe(this.refs.messages);
    this.#schedule();
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#repeat());
  };

  #repeat() {
    if (!this.isConnected) return;
    const { viewport, track, messages } = this.refs;
    // Fill the viewport even when motion starts paused for reduced-motion visitors.
    if (messages.offsetWidth === 0 || getComputedStyle(track).display !== 'flex') return;
    for (const clone of track.querySelectorAll('[data-ribbon-copy]')) clone.remove();
    const width = messages.getBoundingClientRect().width;
    if (!width) return;
    const copies = Math.max(1, Math.ceil(viewport.clientWidth / width));
    for (let i = 1; i < copies * 2; i++) {
      const clone = /** @type {HTMLElement} */ (messages.cloneNode(true));
      clone.removeAttribute('ref');
      clone.setAttribute('data-ribbon-copy', '');
      clone.setAttribute('aria-hidden', 'true');
      for (const element of clone.querySelectorAll('[data-shopify-editor-block], [id], a')) {
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('id');
        if (element instanceof HTMLAnchorElement) element.tabIndex = -1;
      }
      track.append(clone);
    }
    const distance = width * copies;
    this.style.setProperty('--ds-ribbon-distance', `${distance}px`);
    this.style.setProperty('--ds-ribbon-duration', `${distance / Number(this.dataset.speed || 40)}s`);
    this.dataset.ready = 'true';
    if (this.refs.toggle) this.refs.toggle.hidden = false;
  }

  /** Pause or resume motion without changing the ribbon layout. @param {MouseEvent} _event */
  toggleMotion(_event) {
    this.#paused = !this.#paused;
    this.dataset.paused = String(this.#paused);
    this.refs.toggle?.setAttribute('aria-pressed', String(this.#paused));
    if (!this.#paused) this.#schedule();
  }
}

if (!customElements.get('ds-announcement-ribbon')) {
  customElements.define('ds-announcement-ribbon', DSAnnouncementRibbon);
}
